// wizardPersistence.ts - Persistence logic for ProductCreationWizard
import type { ProductData } from "../components/ProductCreationWizard/ProductCreationWizard"
import { PERSISTENCE_CONFIG, createDefaultFormData } from "./wizardConstants"

interface PersistedData extends ProductData {
  timestamp: number
}

// Save form data to localStorage
export function saveFormData(key: string, data: ProductData): void {
  try {
    const dataToSave: PersistedData = {
      ...data,
      timestamp: Date.now()
    }
    localStorage.setItem(key, JSON.stringify(dataToSave))
  } catch (error) {
    console.error("Failed to persist data:", error)
  }
}

// Load form data from localStorage
export function loadFormData(
  key: string,
  initialData?: Partial<ProductData>,
  mode: 'create' | 'edit' = 'create'
): ProductData | null {
  // Only restore localStorage data for create mode, not edit mode
  if (mode === 'edit') {
    return null
  }

  const savedDataStr = localStorage.getItem(key)
  if (!savedDataStr) {
    return null
  }

  try {
    const savedData: PersistedData = JSON.parse(savedDataStr)

    // Check if data is stale
    if (isDataStale(savedData.timestamp)) {
      localStorage.removeItem(key)
      return null
    }

    // Remove timestamp before restoring
    const { timestamp, ...parsed } = savedData

    // Merge with default form structure to ensure all required fields exist
    const restoredData = createDefaultFormData({
      ...initialData,
      ...parsed
    }, 'create')

    return restoredData
  } catch (error) {
    console.error("Failed to load persisted data:", error)
    localStorage.removeItem(key)
    return null
  }
}

// Check if persisted data is stale
export function isDataStale(timestamp: number): boolean {
  return Date.now() - timestamp > PERSISTENCE_CONFIG.STALE_DATA_TIMEOUT
}

// Clear persisted data
export function clearPersistedData(key: string): void {
  localStorage.removeItem(key)
}

// Set reload flag for page refresh detection
export function setReloadFlag(key: string): void {
  sessionStorage.setItem(`${key}${PERSISTENCE_CONFIG.RELOAD_FLAG_SUFFIX}`, "true")
}

// Check and clear reload flag
export function checkAndClearReloadFlag(key: string): boolean {
  const wasReloaded = sessionStorage.getItem(`${key}${PERSISTENCE_CONFIG.RELOAD_FLAG_SUFFIX}`)
  if (wasReloaded) {
    sessionStorage.removeItem(`${key}${PERSISTENCE_CONFIG.RELOAD_FLAG_SUFFIX}`)
    return true
  }
  return false
}

// Clear reload flag without checking
export function clearReloadFlag(key: string): void {
  sessionStorage.removeItem(`${key}${PERSISTENCE_CONFIG.RELOAD_FLAG_SUFFIX}`)
}

// Handle page show event (back/forward navigation)
export function handlePageShow(event: PageTransitionEvent, key: string): void {
  // If page is shown from cache (back/forward) and reload flag exists, clear it
  // This prevents false reload detection when using browser back/forward
  if (event.persisted) {
    clearReloadFlag(key)
  }
}

// Clean up on unmount
export function cleanupOnUnmount(persistData: boolean, key: string): void {
  if (!persistData) return

  // Clear reload flag since we're navigating away (not reloading)
  clearReloadFlag(key)

  // Check if data in localStorage is current (not stale)
  const savedDataStr = localStorage.getItem(key)
  if (savedDataStr) {
    try {
      const savedData: PersistedData = JSON.parse(savedDataStr)
      // If data is current (less than 1 second old), user just left without saving
      if (savedData.timestamp && Date.now() - savedData.timestamp < 1000) {
        // User is leaving the page, clear the data
        localStorage.removeItem(key)
      }
    } catch {
      // If we can't parse, just remove it
      localStorage.removeItem(key)
    }
  }
}

// Check if form has meaningful unsaved data
export function checkForMeaningfulData(data: ProductData): boolean {
  return Boolean(
    data.basicInfo?.productName?.trim() ||
    data.basicInfo?.productSku?.trim() ||
    data.basicInfo?.productDescription?.trim() ||
    data.basicInfo?.productImages?.length > 0 ||
    data.classification?.categoryPath?.length > 0 ||
    data.salesInfo?.variantOptions?.length > 0 ||
    data.salesInfo?.productPrice > 0 ||
    data.salesInfo?.costPrice > 0 ||
    data.shipping?.packageDimensions?.weight > 0
  )
}